/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { trackWeTransferCreativeConversionPixelIfAny } from '@/core/campaigns/WeTransferCreative';
import useStripeMethods from '@/core/stripe/useStripeMethods';
import { useTracker } from '@/core/tracking';
import { getPersistentInfoProvider } from '@/core/tracking/pico/providers';
import { safeLocalStorage } from '@/core/safeLocalStorage/safeLocalStorage';
import warningInfo from '@/public/images/icons/warning_info.svg';
import warningInfoWhite from '@/public/images/icons/warning_info_white.svg';
import { useLocalization } from '@/services/localizationService';
import { useLocaleDateFormatter } from '@/services/localizationService/useLocaleDateFormatter';
import {
  PaymentPlatform,
  PaymentStrategiesIds,
  SUBSCRIPTION_INTRO_PRICE_ID_STORAGE_KEY,
} from '@/services/paymentService';
import {
  isBusySelector,
  lockUISelector,
  unlockUISelector,
} from '@/stores/appStore';
import { closeModalSelector, pushModalSelector } from '@/stores/modalStore';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import settingsState from '@/stores/settingsStore';
import Spinner from '@/ui/content/Spinner';
import { Form } from '@/ui/form';
import * as Sentry from '@sentry/nextjs';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  trackCompletedEvent,
  trackEvent,
  trackFailedEvent,
  trackStartedEvent,
} from '@/core/monitoring/trackEvents';
import { useReCaptcha } from '@/core/recaptcha/useReCaptcha';
import { useConsent } from '@/features/userConsent';
import {
  useMediaService,
  usePaymentService,
  usePreferencesService,
  usePriceService,
} from '@/layout/appWrapper/ServiceProvider';
import { Price } from '@/services/priceService';
import { useUser } from '@/services/userService';
import {
  isReminderEnabledState,
  onboardingState,
  offerPageVariantState,
  showFreeTrialSelector,
} from '@/stores/onboardingStore';
import CountryNotAllowedModal from '@/features/payment/modals/CountryNotAllowedModal';
import maybeShowSuccessToast from './maybeShowSuccessToast';
import { ErrorModal } from './modals';

interface Props {
  selectedPrice: Price;
  onSubmit: CallableFunction;
  willDownloadAutomatically: boolean;
  isIntroPriceActive?: boolean;
  introPriceId?: string;
  webOnboardingUserId?: string;
  webOnboardingLocalStorageId?: string;
  paywallVersion?: string;
}

export default function PaymentInformationForm({
  selectedPrice,
  onSubmit,
  willDownloadAutomatically,
  isIntroPriceActive = false,
  introPriceId,
  webOnboardingUserId,
  webOnboardingLocalStorageId,
  paywallVersion,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();

  const subscriptionIntroPriceId = isIntroPriceActive
    ? introPriceId || process.env.NEXT_PUBLIC_STRIPE_INTRO_PRICE_ID
    : undefined;

  const lockUI = useSetRecoilState(lockUISelector);
  const unlockUI = useSetRecoilState(unlockUISelector);
  const isBusy = useRecoilValue(isBusySelector);
  const { catchStripePriceError, trackPayment } = usePaymentService();
  const { useFormatRecurringPrice, usePricesByIds } = usePriceService();
  const { data: introPrices } = usePricesByIds(
    introPriceId ? [introPriceId] : []
  );
  const introPrice = introPrices?.[0];

  const router = useRouter();
  const { taskId } = router.query;

  const pushModal = useSetRecoilState(pushModalSelector);
  const closeModal = useSetRecoilState(closeModalSelector);
  const { storeNewPreferences } = usePreferencesService();

  const { data: user, mutate: mutateUser } = useUser();

  const { useMedia } = useMediaService();
  const { getMediaAndUpdateStore } = useMedia();

  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const { track } = useTracker();
  const { t, data } = useLocalization();
  const { dateToLocaleShort } = useLocaleDateFormatter();
  const { isReminderEnabled, selectedPaymentStrategy } =
    useRecoilValue(onboardingState);
  const setIsReminderEnabled = useSetRecoilState(isReminderEnabledState);

  const shouldShowFreeTrial = useRecoilValue(showFreeTrialSelector);
  const offerPageVariant = useRecoilValue(offerPageVariantState);
  const offerPlan =
    offerPageVariant === 'pcp'
      ? selectedPrice.recurringInterval === 'year'
        ? 'yearly'
        : 'weekly'
      : offerPageVariant === 'ocp'
      ? shouldShowFreeTrial
        ? 'free'
        : 'welcome'
      : undefined;

  const [isPaymentButtonEnabled, setIsPaymentButtonEnabled] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

  const settings = useRecoilValue(settingsState);
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);

  const { shouldShowMarketingConsentModal } = useConsent();

  const { disableEmailReminder } = settings.values;
  const isOcpPreview =
    offerPageVariant === 'ocp' &&
    process.env.NODE_ENV === 'development' &&
    !settings.values.ocp2wIntroPriceId;

  useReCaptcha();

  const overrideEmailReminder = useCallback(async () => {
    if (!user) {
      return;
    }

    if (disableEmailReminder) {
      await storeNewPreferences({
        ...user.preferences,
        wantsEmailReminder: false,
      });
      return;
    }

    await storeNewPreferences({
      ...user?.preferences,
      wantsEmailReminder: isReminderEnabled,
    });
  }, [user, disableEmailReminder, isReminderEnabled, storeNewPreferences]);

  const forceFreeTrial = useMemo(() => {
    if (!webOnboardingUserId) {
      return false;
    }

    return paywallVersion === 'ft';
  }, [paywallVersion, webOnboardingUserId]);
  const { subscribe: stripeSubscribe } = useStripeMethods(
    stripe!,
    mutateUser,
    forceFreeTrial ||
      ((offerPageVariant !== 'control' || settings.values.isFreeTrialEnabled) &&
        shouldShowFreeTrial) ||
      false,
    isIntroPriceActive ? subscriptionIntroPriceId : undefined,
    selectedPrice?.id,
    selectedPrice.exports,
    offerPlan,
    offerPageVariant === 'control' ? undefined : offerPageVariant
  );

  const onStripeSubmit = useCallback(async () => {
    catchStripePriceError(stripe, elements, selectedPrice?.id);
    if (!stripe || !elements) {
      return;
    }

    await overrideEmailReminder();
    lockUI({});

    const payTrackingInfo = {
      paymentPlatform: selectedPaymentMethod,
      paymentStrategy: 'subscription',
    };

    try {
      setIsLoadingPayment(true);
      trackPayment({
        track,
        status: 'selected',
        paymentPlatform: selectedPaymentMethod as PaymentPlatform,
        priceId: selectedPrice?.id,
        freeTrialEnabled: shouldShowFreeTrial,
        emailReminderEnabled: isReminderEnabled,
        paymentStrategy: 'subscription',
        webOnboardingUserId,
        webOnboardingLocalStorageId,
        paywallVersion,
        previousAction:
          offerPageVariant === 'control' ? undefined : offerPageVariant,
        offerPlan,
      });

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const persistedLandingCampaignNameQueryValue: string | undefined =
        getPersistentInfoProvider().getInfo().t_crname;
      if (persistedLandingCampaignNameQueryValue) {
        trackWeTransferCreativeConversionPixelIfAny({
          creativeName: persistedLandingCampaignNameQueryValue,
        });
      }
      trackStartedEvent(['payment'], payTrackingInfo);
      let showSuccessToast = true;

      try {
        const status = await stripeSubscribe({
          elements,
          isEmailReminderEnabled: isReminderEnabled,
          paymentStrategyId: selectedPaymentStrategy?.id || 'base',
          willDownloadAutomatically,
        });

        const isSubscriptionCreated =
          status === 'active' || status === 'trialing' || status === 'past_due';
        if (!isSubscriptionCreated) {
          showSuccessToast = false;
          pushModal({
            id: 'creationError',
            Modal: ErrorModal,
            error: {
              message: t(data.account.incompleteSubscriptionCreatedMessage),
            },
          });
        }
        trackCompletedEvent(['payment'], payTrackingInfo);
        if (isSubscriptionCreated && subscriptionIntroPriceId && user) {
          safeLocalStorage.setItem(
            `${SUBSCRIPTION_INTRO_PRICE_ID_STORAGE_KEY}:${user.webUserId}`,
            subscriptionIntroPriceId
          );
        }
      } catch (e) {
        trackFailedEvent(['payment'], {
          ...payTrackingInfo,
          error: e.toString(),
        });
        throw e;
      }

      closeModal('payment');

      trackPayment({
        track,
        status: 'completed',
        paymentPlatform: selectedPaymentMethod as PaymentPlatform,
        priceId: selectedPrice?.id,
        freeTrialEnabled: shouldShowFreeTrial,
        emailReminderEnabled: isReminderEnabled,
        paymentStrategy: 'subscription',
        webOnboardingUserId,
        webOnboardingLocalStorageId,
        paywallVersion,
        previousAction:
          offerPageVariant === 'control' ? undefined : offerPageVariant,
        offerPlan,
      });

      await mutateUser();

      if (taskId) {
        await getMediaAndUpdateStore();
      }
      if (showSuccessToast) {
        maybeShowSuccessToast({
          selectedPaymentStrategy,
          settings,
          pushModal,
          shouldShowMarketingConsentModal,
          message: data.onboarding.congratsMember,
          webOnboardingUserId,
          hasEditableMedia: !!mediaInComparer.id,
        });
      }
      onSubmit();
    } catch (e) {
      Sentry.captureException(e);

      if (e.status === 403 && e.message.startsWith('Web subscriptions')) {
        pushModal({
          id: 'forbiddenCountryError',
          Modal: CountryNotAllowedModal,
        });
      } else {
        pushModal({ id: 'paymentError', Modal: ErrorModal, error: e });
      }
    }
    setIsLoadingPayment(false);
    unlockUI({});
  }, [
    catchStripePriceError,
    closeModal,
    data.account.incompleteSubscriptionCreatedMessage,
    data.onboarding.congratsMember,
    elements,
    getMediaAndUpdateStore,
    isReminderEnabled,
    lockUI,
    mutateUser,
    onSubmit,
    offerPageVariant,
    offerPlan,
    overrideEmailReminder,
    paywallVersion,
    pushModal,
    selectedPaymentMethod,
    selectedPaymentStrategy,
    selectedPrice?.id,
    settings,
    shouldShowFreeTrial,
    shouldShowMarketingConsentModal,
    stripe,
    stripeSubscribe,
    t,
    taskId,
    track,
    trackPayment,
    unlockUI,
    webOnboardingLocalStorageId,
    webOnboardingUserId,
    willDownloadAutomatically,
  ]);

  const onElementChange = useCallback((event) => {
    setIsPaymentButtonEnabled(!!event.complete);
    setSelectedPaymentMethod(event.value.type);
  }, []);

  useEffect(() => {
    if (!selectedPrice) {
      return;
    }

    trackEvent(['paywall', 'shown'], {
      type: 'payment_step',
    });
  }, [selectedPrice]);

  const recurringFormatted = useFormatRecurringPrice(selectedPrice);
  const shouldShowOfferDisclosure =
    offerPageVariant !== 'control' || isIntroPriceActive;
  const shouldShowWelcomeDisclosure =
    offerPlan === 'welcome' || isIntroPriceActive;
  const welcomeOfferPrice = useFormatRecurringPrice(
    offerPlan === 'welcome' ? introPrice : { ...selectedPrice, amountCents: 99 }
  ).split(/\/| every /)[0];
  const nextChargeDate = useMemo(() => {
    const d = new Date();
    if (offerPlan === 'welcome' && introPrice) {
      if (introPrice.recurringInterval === 'week') {
        d.setDate(d.getDate() + introPrice.recurringIntervalCount * 7);
      } else if (introPrice.recurringInterval === 'month') {
        d.setMonth(d.getMonth() + introPrice.recurringIntervalCount);
      } else {
        d.setFullYear(d.getFullYear() + introPrice.recurringIntervalCount);
      }
    } else if (shouldShowFreeTrial || isIntroPriceActive) {
      d.setDate(d.getDate() + 7);
    } else if (selectedPrice.recurringInterval === 'week') {
      d.setDate(d.getDate() + selectedPrice.recurringIntervalCount * 7);
    } else if (selectedPrice.recurringInterval === 'month') {
      d.setMonth(d.getMonth() + selectedPrice.recurringIntervalCount);
    } else {
      d.setFullYear(d.getFullYear() + selectedPrice.recurringIntervalCount);
    }
    return dateToLocaleShort(d);
  }, [
    dateToLocaleShort,
    isIntroPriceActive,
    introPrice,
    offerPlan,
    selectedPrice,
    shouldShowFreeTrial,
  ]);

  if (!selectedPrice) {
    return (
      <div className="flex justify-center items-center">
        <TailSpin color="black" height="50" width="50" aria-label="loading" />
      </div>
    );
  }

  const stripeButtonText = shouldShowOfferDisclosure
    ? data.common.completeCheckout
    : webOnboardingUserId
    ? data.common.continue
    : shouldShowFreeTrial
    ? data.onboarding.startForFree
    : isIntroPriceActive
    ? data.common.completeCheckout
    : data.common.subscribe;

  const paymentElementOptions: StripePaymentElementOptions = {
    paymentMethodOrder: ['paypal', 'apple_pay', 'google_pay', 'card'],
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
    terms: {
      card: 'never',
      paypal: 'never',
      googlePay: 'never',
      applePay: 'never',
    },
  };

  const isBusinessAccount =
    selectedPaymentStrategy?.id === PaymentStrategiesIds.Business;

  return (
    <Form className="space-y-7" onSubmit={onStripeSubmit} disabled={isBusy}>
      <div className="space-y-4 pb-10">
        <PaymentElement
          className="rounded-xl M18 mt-2"
          onChange={onElementChange}
          options={paymentElementOptions}
        />
        {shouldShowOfferDisclosure && (
          <div className="R14 leading-5 text-overlay-black-80 bg-gray-100 border border-overlay-white-15 rounded-2xl p-4 space-y-3 mt-2">
            <p>
              {shouldShowWelcomeDisclosure
                ? t(data.common.paymentDisclosure1, {
                    cta: t(stripeButtonText),
                    introPrice: welcomeOfferPrice,
                    nextChargeDate,
                    recurring: recurringFormatted,
                    tos: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="https://support.bendingspoons.com/tos?app=1470373330"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                  })
                : t(
                    shouldShowFreeTrial
                      ? data.common.paymentDisclosureFreeTrial
                      : data.common.paymentDisclosureRecurring,
                    {
                      cta: t(stripeButtonText),
                      nextChargeDate,
                      recurring: recurringFormatted,
                      tos: (text) => (
                        <a
                          style={{ color: '#0000FF' }}
                          className="underline"
                          href="https://support.bendingspoons.com/tos?app=1470373330"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {text}
                        </a>
                      ),
                    }
                  )}
            </p>
            <p>{t(data.common.paymentDisclosure2)}</p>
            <p>
              {isBusinessAccount
                ? t(data.common.paymentDisclosure3BusinessAccount, {
                    tos: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="https://support.bendingspoons.com/tos?app=1470373330"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                    additional: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="/docs/remini-business-additional-terms.pdf"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                    privacy: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="https://support.bendingspoons.com/privacy?app=1470373330"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                  })
                : t(data.common.paymentDisclosure3, {
                    tos: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="https://support.bendingspoons.com/tos?app=1470373330"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                    privacy: (text) => (
                      <a
                        style={{ color: '#0000FF' }}
                        className="underline"
                        href="https://support.bendingspoons.com/privacy?app=1470373330"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {text}
                      </a>
                    ),
                  })}
            </p>
          </div>
        )}
        {shouldShowFreeTrial ? (
          <div
            className={`${
              webOnboardingUserId ? 'text-gray-300' : 'text-overlay-black-60'
            } text-xs inline-flex`}
          >
            {webOnboardingUserId ? (
              <img {...warningInfoWhite} alt="Warning info" />
            ) : (
              <img {...warningInfo} alt="Warning info" />
            )}
            <span className="M12 ps-1.5">
              {t(data.onboarding.enterCardInfo)}
            </span>
          </div>
        ) : null}
        <p className={`R11 text-gray-300 `}>
          {t(data.common.recaptcha, {
            pp: (text) => (
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                {text}
              </a>
            ),
            tos: (text) => (
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noreferrer"
              >
                {text}
              </a>
            ),
          })}
        </p>

        {webOnboardingUserId && (
          <div className="pt-8 w-full transition-opacity duration-300">
            <input
              className="sr-only"
              type="checkbox"
              id="enable-email-reminder"
              checked={isReminderEnabled}
              onChange={() => {
                const newVal = !isReminderEnabled;
                track(
                  'paywall_email_reminder',
                  newVal ? 'enabled' : 'disabled',
                  {
                    webOnboardingUserId,
                    webOnboardingLocalStorageId,
                    paywallVersion,
                  }
                );
                setIsReminderEnabled(newVal);
              }}
            />
            <label
              className="bg-overlay-white-10 rounded-3xl w-full min-h-18 flex items-center space-s-2 px-6 py-2 cursor-pointer"
              htmlFor="enable-email-reminder"
            >
              <div className="flex-1 space-y-1">
                <h3 className="SB15 transition-all text-white">
                  {isReminderEnabled ? (
                    t(data.onboarding.reminderEnabled)
                  ) : (
                    <>{t(data.onboarding.enableReminder)} 🔔</>
                  )}
                </h3>
                <p className="M12 transition-colors text-gray-300">
                  {isReminderEnabled
                    ? t(data.account.willNotify)
                    : t(data.account.getNotified)}
                </p>
              </div>
              <div
                role="presentation"
                className={`rounded-full transition-all ${
                  isReminderEnabled ? 'bg-green' : 'bg-gray-300 bg-opacity-50'
                }`}
                style={{ width: 38, height: 22, padding: 3 }}
              >
                <div
                  className="transition-all duration-100 ease-out"
                  style={{ width: isReminderEnabled ? '100%' : 16 }}
                >
                  <div
                    className="bg-gray-500 rounded-full ms-auto drop-shadow-sm active:!w-7 transition-all"
                    style={{ width: 16, height: 16 }}
                  />
                </div>
              </div>
            </label>
          </div>
        )}

        <Form.Button
          className={`w-full btn btn--xl shadow-dark-button ${
            webOnboardingUserId ? 'btn--white' : 'btn--black'
          }`}
          disabled={!isPaymentButtonEnabled || isOcpPreview}
        >
          <p className="SB18">{t(stripeButtonText)}</p>

          {isLoadingPayment && (
            <Spinner
              color={webOnboardingUserId ? 'black' : 'white'}
              size={20}
            />
          )}
        </Form.Button>

        {webOnboardingUserId && (
          <p className="R14 text-gray-300 text-center">
            By tapping on Continue, you accept our{' '}
            <a
              className="underline text-[#0A7AFF]"
              target="_blank"
              href={
                selectedPaymentStrategy?.id === PaymentStrategiesIds.Business
                  ? '/docs/remini-business-additional-terms.pdf'
                  : 'https://bendingspoons.com/tos.html?app=1470373330'
              }
              rel="noreferrer"
            >
              {selectedPaymentStrategy?.id === PaymentStrategiesIds.Business
                ? t(data.common.additionalTerms)
                : t(data.common.tos)}
            </a>
            .
          </p>
        )}
      </div>
    </Form>
  );
}
